import React, { useEffect, useState } from 'react';
import Accordion from 'components/accordion/Accordion';
import { APP_STATUS, AppState, SelectPaymentMethodChangeStatus } from 'config/config';
import { AuthorizationType, availablePmpt, DisableReason, PaymentCategoryType } from 'consts';
import withLocaleContext from 'hoc/withLocaleContext';
import { getAllPaymentData } from 'services/Api';
import License from 'types/License';
import queryString from 'query-string';
import ActivePaymentMethod from './ActivePaymentMethod';
import withLicenseNoteContext from '../../hoc/withLicenseNoteContext';
import withTextContext from '../../hoc/withTextContext';
import withSelectedMethodContext from '../../hoc/withSelectedMethodContext';
import withCardContext from '../../hoc/withCardContext';
import getDeviceFingerPrint from './fingerprint/FingerPrint';
import { IPaymentData } from '../../types/Payment';
import { IAppPaymentProps } from './AppPayment.types';
import { filterIfHasSelectedPaymentMethod } from './utils/FilterIfHasSelectedPaymentMethod';
import checkIfSelectedMethodIsDisabledAndFindReason from './utils/DisabledMethodValidator';
import convertPaymentMethod from './utils/PaymentMethodConverter';
import withAppStateContext from '../../hoc/withAppStateContext';

const PROCESS_ENV = process.env;

const getDefaultPaymentMethodType = (paymentList: IPaymentMethod[]) => {
  const activePaymentMethod = new ActivePaymentMethod(paymentList);
  return activePaymentMethod.getActivePaymentMethod();
};

function isMethodWithoutChangeOption(paymentData: IPaymentData) {
  return (
    (paymentData.payment.selectedPaymentMethodType === PaymentCategoryType.BLIK &&
      paymentData.payment.authorizationType === AuthorizationType.CODE) ||
    paymentData.payment.selectedPaymentMethodType === PaymentCategoryType.CARD ||
    paymentData.payment.selectedPaymentMethodType === PaymentCategoryType.CARD_RECURRING ||
    paymentData.payment.selectedPaymentMethodType === PaymentCategoryType.CLICK_TO_PAY
  );
}

function isLevelZero(
  isSelectedPaymentMethodDisabled: boolean,
  isPaymentMethodSelected: '' | string,
  setLevelZero,
  paymentData: IPaymentData
) {
  return (
    !isSelectedPaymentMethodDisabled &&
    isPaymentMethodSelected &&
    setLevelZero &&
    paymentData.payment.status === APP_STATUS.NEW
  );
}

function isLevelZeroMethodWithChangeMethodOption(selectPaymentMethodChangeStatus, paymentData: IPaymentData) {
  return (selectPaymentMethodChangeStatus !== SelectPaymentMethodChangeStatus.NOT_APPLICABLE &&
    !isMethodWithoutChangeOption(paymentData)) as boolean;
}

function AppPayment(props: IAppPaymentProps) {
  const [paymentMethodTypes, setPaymentMethodTypes] = useState<IPaymentMethod[]>([]);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<string | undefined>(undefined);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | undefined>(undefined);
  const [defaultPaymentMethodType, setDefaultPaymentMethodType] = useState<string | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);

  const {
    match: {
      params: { paymentId },
    },
    setCardEnabled,
    setPaymentDetails,
    setSelectedPaymentMethodDisabledAndReason,
    setLicense,
    setLevelZero,
    setLocaleFromPaymentRequest,
    setSavedInstruments,
    history,
    selectPaymentMethodChangeStatus,
    setSelectedPaymentMethodChange,
  } = props;

  const isPaymentDataNotLoaded = () => paymentMethodTypes.length === 0;

  useEffect(() => {
    const { setAppState } = props;
    setAppState?.(AppState.PAYMENT);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      let isApplePaySupported: boolean;
      try {
        isApplePaySupported = (window.ApplePaySession && window.ApplePaySession.canMakePayments?.()) || false;
      } catch (e) {
        isApplePaySupported = false;
      }

      try {
        const fingerPrint = await getDeviceFingerPrint(PROCESS_ENV);
        const paymentData: IPaymentData = await getAllPaymentData(paymentId, isApplePaySupported, fingerPrint);

        const [
          isSelectedPaymentMethodDisabled,
          selectedPaymentMethodDisableReason,
        ] = checkIfSelectedMethodIsDisabledAndFindReason(paymentData);

        if (setSelectedPaymentMethodDisabledAndReason) {
          setSelectedPaymentMethodDisabledAndReason(
            isSelectedPaymentMethodDisabled,
            selectedPaymentMethodDisableReason || DisableReason.METHOD_DISABLED
          );
        }

        const shouldPaymentMethodBeSelected = !isSelectedPaymentMethodDisabled;

        if (shouldPaymentMethodBeSelected) {
          setSelectedPaymentMethodType(paymentData.payment.selectedPaymentMethodType);
          setSelectedPaymentMethodId(paymentData.payment.selectedPaymentMethodId);
          setDefaultPaymentMethodType(getDefaultPaymentMethodType(paymentData.paymentMethodTypes));
        }

        setPaymentMethodTypes(paymentData.paymentMethodTypes);

        const { token } = queryString.parse(window.location.search) || {};

        if (!token || !paymentId) {
          setError(new Error('No token or payment id'));
        }

        const isPaymentMethodSelected =
          paymentData.payment.selectedPaymentMethodType && paymentData.payment.selectedPaymentMethodId;

        if (isLevelZero(isSelectedPaymentMethodDisabled, isPaymentMethodSelected, setLevelZero, paymentData)) {
          // @ts-ignore
          setLevelZero(true);

          if (setSelectedPaymentMethodChange) {
            if (isLevelZeroMethodWithChangeMethodOption(selectPaymentMethodChangeStatus, paymentData)) {
              setSelectedPaymentMethodChange(SelectPaymentMethodChangeStatus.CAN_BE_CHANGED);
            } else {
              setSelectedPaymentMethodChange(SelectPaymentMethodChangeStatus.NOT_APPLICABLE);
            }
          }
        }

        if (paymentData.payment.status === APP_STATUS.PENDING && !paymentData.payment.redirectUrl) {
          return history.push(`/${paymentId}/status?token=${token}`);
        }

        if (![APP_STATUS.NEW, APP_STATUS.PENDING].includes(paymentData.payment.status)) {
          return history.push(`/${paymentId}/callback?token=${token}`);
        }

        const cardPaymentMethod = paymentData.paymentMethodTypes.filter(pm => pm.type === PaymentCategoryType.CARD);
        if (
          cardPaymentMethod.length > 0 &&
          setCardEnabled &&
          paymentData.payment.selectedPaymentMethodId === cardPaymentMethod[0].paymentMethods[0].id
        ) {
          setCardEnabled(true, cardPaymentMethod[0].paymentMethods[0].pmpt);
        }

        const recurringCardPaymentMethod = paymentData.paymentMethodTypes.filter(
          pm => pm.type === PaymentCategoryType.CARD_RECURRING
        );
        if (
          recurringCardPaymentMethod.length > 0 &&
          setCardEnabled &&
          paymentData.payment.selectedPaymentMethodId === recurringCardPaymentMethod[0].paymentMethods[0].id
        ) {
          setCardEnabled(true, recurringCardPaymentMethod[0].paymentMethods[0].pmpt);
        }

        setPaymentDetails(paymentData.payment, paymentId);

        if (setLicense) {
          setLicense(paymentData.payment.license || License.MBANK);
        }

        if (setLocaleFromPaymentRequest) {
          setLocaleFromPaymentRequest(paymentData.payment.locale);
        }

        if (setSavedInstruments) {
          const savedInstruments =
            paymentData.paymentMethodTypes
              .find(it => it.type === PaymentCategoryType.CARD)
              ?.paymentMethods?.find(pm => pm.pmpt === availablePmpt.WORLDLINE)?.savedInstruments || [];
          setSavedInstruments(savedInstruments);
        }
        return paymentData;
      } catch (e) {
        setError(e);
      }
      return undefined;
    };

    if (isPaymentDataNotLoaded()) {
      fetchData().then();
    }
  }, [
    paymentId,
    setCardEnabled,
    setSelectedPaymentMethodDisabledAndReason,
    setLicense,
    setLevelZero,
    setLocaleFromPaymentRequest,
    setSavedInstruments,
  ]);

  useEffect(() => {
    if (
      selectPaymentMethodChangeStatus === SelectPaymentMethodChangeStatus.CHANGED &&
      selectedPaymentMethodType !== undefined &&
      selectedPaymentMethodId !== undefined &&
      setSelectedPaymentMethodChange
    ) {
      setSelectedPaymentMethodType(undefined);
      setSelectedPaymentMethodId(undefined);
      setDefaultPaymentMethodType(undefined);
    }
  }, [selectPaymentMethodChangeStatus, selectedPaymentMethodType, selectedPaymentMethodId]);

  useEffect(() => {
    const cardPaymentMethod = paymentMethodTypes.filter(pm => pm.type === PaymentCategoryType.CARD);
    if (cardPaymentMethod.length > 0 && setCardEnabled && !(selectedPaymentMethodType && selectedPaymentMethodId)) {
      setCardEnabled(true, cardPaymentMethod[0].paymentMethods[0].pmpt);
    }
  }, [selectedPaymentMethodType, selectedPaymentMethodId, paymentMethodTypes]);

  const accordionItems = convertPaymentMethod(
    filterIfHasSelectedPaymentMethod(paymentMethodTypes, selectedPaymentMethodType, selectedPaymentMethodId)
  );

  function hasGroupedCards(): boolean {
    const cardPaymentMethod = paymentMethodTypes.filter(pm => pm.type === PaymentCategoryType.CARD)[0];
    return cardPaymentMethod ? cardPaymentMethod.paymentMethods.length > 1 : false;
  }

  if (!paymentId) {
    throw new Error('No payment id');
  }

  if (error) {
    throw error;
  }

  return (
    <Accordion
      items={accordionItems}
      selectedPaymentMethodType={selectedPaymentMethodType}
      defaultPaymentMethodType={defaultPaymentMethodType}
      hasGroupedCardMethods={hasGroupedCards()}
    />
  );
}

export default withLicenseNoteContext(
  withSelectedMethodContext(withTextContext(withLocaleContext(withCardContext(withAppStateContext(AppPayment)))))
);
